import { MARKET_TZ, zonedWallTimeToInstant } from "../constants/marketSchedule";
import { normalizeMarketZone } from "./marketSupport";

const QUARTER_HOUR_MS = 15 * 60 * 1000;
export const MARKET_DELIVERY_TIME_ZONE = "Europe/Brussels";

export function validDeliveryDay(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ""))) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

export function operationalTimeZone(config = {}) {
  const market = normalizeMarketZone(config.biddingZone || config.market || config.bidding_zone);
  const candidate = MARKET_TZ[market] || (market === "BG" ? "Europe/Sofia" : null) || config.timeZone || config.timezone || "UTC";
  try {
    new Intl.DateTimeFormat("en-GB", { timeZone: candidate }).format();
    return candidate;
  } catch {
    return "UTC";
  }
}

export function deliveryDayInZone(value, timeZone = "UTC") {
  if (validDeliveryDay(value)) return value;
  const date = value instanceof Date ? value : new Date(value);
  if (!value || !Number.isFinite(date.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone, year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(date);
  const part = (name) => parts.find((item) => item.type === name)?.value;
  return `${part("year")}-${part("month")}-${part("day")}`;
}

export function deliveryDayBounds(day, timeZone) {
  if (!validDeliveryDay(day)) return null;
  const [year, month, date] = day.split("-").map(Number);
  const next = new Date(Date.UTC(year, month - 1, date + 1));
  const start = zonedWallTimeToInstant(timeZone, year, month, date, 0, 0);
  const endExclusive = zonedWallTimeToInstant(timeZone, next.getUTCFullYear(), next.getUTCMonth() + 1, next.getUTCDate(), 0, 0);
  return { start, endExclusive, intervalCount: (endExclusive - start) / QUARTER_HOUR_MS };
}

export function deliveryDayFetchRange(day, assetTimeZone) {
  const bounds = deliveryDayBounds(day, MARKET_DELIVERY_TIME_ZONE);
  if (!bounds) return { start: "", end: "" };
  return {
    start: deliveryDayInZone(bounds.start, assetTimeZone),
    end: deliveryDayInZone(new Date(bounds.endExclusive - 1), assetTimeZone),
  };
}

export function deliveryTimestampDisplay(value, timeZone) {
  const date = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(date.getTime())) return { dateKey: "", short: "", long: "" };
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZoneName: "shortOffset",
  }).formatToParts(date);
  const part = (name) => parts.find((item) => item.type === name)?.value;
  const clock = `${part("hour")}:${part("minute")} ${part("timeZoneName")}`;
  return {
    dateKey: `${part("year")}-${part("month")}-${part("day")}`,
    short: `${part("day")}/${part("month")}, ${clock}`,
    long: `${part("day")}/${part("month")}/${part("year")}, ${clock}`,
  };
}

function runInput(run) {
  if (typeof run.input_payload !== "string") return run.input_payload || {};
  try { return JSON.parse(run.input_payload) || {}; } catch { return {}; }
}

// Old runs may cover several dates. Never relabel their whole range as one day.
export function runDeliveryPeriod(run = {}, fallbackTimeZone = "UTC") {
  const input = runInput(run);
  const timeZone = run.timezone || run.tz || input.timezone || input.tz || fallbackTimeZone;
  const explicitDay = run.delivery_day || run.deliveryDay || input.delivery_day;
  if (validDeliveryDay(explicitDay)) return { start: explicitDay, end: explicitDay, legacyRange: false, timeZone };
  const startValue = run.simulation_start || run.simulationStart || run.sim_start || run.simStart || run.start_date || run.startDate || run.range_start || run.rangeStart || run.simulation_window_start || run.window_start || run.windowStart || run.request_start || run.requestStart || run.inputs?.start || run.inputs?.start_date || run.payload?.start || run.payload?.start_date || run.data?.start || run.data?.start_date || run.params?.start || run.params?.start_date || input.start || input.start_date;
  const endValue = run.simulation_end || run.simulationEnd || run.sim_end || run.simEnd || run.end_date || run.endDate || run.range_end || run.rangeEnd || run.simulation_window_end || run.window_end || run.windowEnd || run.request_end || run.requestEnd || run.inputs?.end || run.inputs?.end_date || run.payload?.end || run.payload?.end_date || run.data?.end || run.data?.end_date || run.params?.end || run.params?.end_date || input.end || input.end_date;
  const dateKey = (value) => {
    // Legacy timestamps without an offset were already local wall times.
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}(?:[ T]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)?$/.test(value)) return value.slice(0, 10);
    return deliveryDayInZone(value, timeZone);
  };
  const start = dateKey(startValue);
  let end = dateKey(endValue) || start;
  if (start && end > start && typeof endValue === "string" && endValue.length > 10) {
    const parts = new Intl.DateTimeFormat("en-GB", { timeZone, hourCycle: "h23", hour: "2-digit", minute: "2-digit", second: "2-digit" });
    const localMidnight = /[zZ]|[+-]\d{2}:?\d{2}$/.test(endValue)
      ? Number.isFinite(new Date(endValue).getTime()) && parts.format(new Date(endValue)) === "00:00:00"
      : /[ T]00:00(?::00(?:\.0+)?)?$/.test(endValue);
    if (localMidnight) {
      const previous = new Date(`${end}T00:00:00Z`);
      previous.setUTCDate(previous.getUTCDate() - 1);
      end = previous.toISOString().slice(0, 10);
    }
  }
  return { start, end, legacyRange: Boolean(start && end && start !== end), timeZone };
}

export function formatRunDeliveryPeriod(run, timeZone) {
  const period = runDeliveryPeriod(run, timeZone);
  const formatDay = (day) => day ? day.split("-").reverse().join("/") : "N/A";
  return period.legacyRange
    ? `${formatDay(period.start)} – ${formatDay(period.end)} (legacy range)`
    : formatDay(period.start);
}

export function runIncludesDeliveryDay(run, day, timeZone) {
  const period = runDeliveryPeriod(run, timeZone);
  return Boolean(period.start && day >= period.start && day <= (period.end || period.start));
}
